import type { ReactNode } from "react";

import { Avatar, firstNameOf, type AvatarSize } from "./Avatar.js";
import { AvatarGroup, type AvatarGroupPerson } from "./AvatarGroup.js";
import { cn } from "../lib/cn.js";

/**
 * A person's face and their name, side by side — the shape twelve surfaces need.
 *
 * ==========================================================================================
 * `isPerson` EXISTS SO THE HARD RULE IS DECIDED ONCE
 * ==========================================================================================
 *
 * Every table in this application has rows that print a name-shaped label for something that is not
 * a person: a savings account owned by the household says "Joint", an overnight price fetch says
 * "Daily update", a snapshot whose recorder deleted their account says "A former member". None of
 * those is somebody's name, and drawing initials from one produces "AM" — nobody.
 *
 * `Avatar` already expresses that by making `name` optional. The reason for a second component on
 * top is that twelve call sites would each have to remember to translate their own null into an
 * omitted prop, and a call site that forgets does not fail — it renders a plausible-looking circle
 * with the wrong letters in it. Here the translation happens once, and a caller states the fact it
 * actually knows: whether this row is about a person.
 *
 * ==========================================================================================
 * THE NAME IS ALWAYS TEXT, AND BESIDE A FACE IT IS THE FIRST NAME
 * ==========================================================================================
 *
 * FR-018. The picture never replaces the label, so a row remains readable to somebody who cannot
 * see it, and the `Avatar` stays decorative — `alt=""` with no duplicate announcement.
 *
 * What it prints is the FIRST name: "(AL) Ada" rather than "(AL) Ada Lovelace". The face is doing
 * the identifying, and the surname repeated down a column is width spent on nothing. The circle
 * still carries both initials, so two members called Ada remain distinguishable, and the full name
 * is on the element's `title` for anyone who wants it.
 *
 * A STAND-IN IS PRINTED WHOLE. "A former member" truncated to its first word is "A", which is not a
 * shorter label — it is a different one, and a meaningless one. So the shortening applies only when
 * `isPerson`, which is the same flag that decides whether a mark is drawn at all.
 */

export interface PersonLabelProps {
  /** What to print. Always shown, whether or not it names a person. */
  label: string;
  /**
   * Whether `label` is somebody's NAME.
   *
   * False for "Joint", "Daily update", "A former member" and every other stand-in.
   *
   * Those are NOT all the same fact, which is what `group` is for. "Daily update" and "A former
   * member" have nobody behind them and get no mark. A household-owned record has EVERYBODY behind
   * it, and gets all their faces.
   *
   * REQUIRED, AND IT WAS OPTIONAL FOR AN HOUR. Defaulting it to `true` meant a caller who forgot it
   * got initials for a stand-in — "J" beside "Joint", "AM" beside "A former member" — which renders
   * cleanly, reads as data, and fails no test. Every table in the application has at least one such
   * row, so the default was wrong more often than it was right.
   *
   * Making it required moves that from something a reviewer has to notice to something the compiler
   * refuses, which is the same trade `Table.Cell`'s typed `testId` makes one file along.
   */
  isPerson: boolean;
  /**
   * Their picture, when they have one and are still identifiable.
   *
   * `| undefined` IS EXPLICIT because `exactOptionalPropertyTypes` is on and every call site
   * produces one the same way — `members.find(…)?.avatarUrl` is `undefined` when the person is no
   * longer a member and `null` when they are but have set no picture. Both mean "no picture" here,
   * and requiring twelve callers to write `?? null` would be noise around a distinction this
   * component does not draw.
   */
  avatarUrl?: string | null | undefined;
  /**
   * The people a NON-PERSON row belongs to, when it belongs to several — a joint account, a shared
   * income source, a household card.
   *
   * Ignored when `isPerson`, because a row about one person already has its face. Absent on a row
   * that is about nobody, which is how "Daily update" stays blank while "Joint" fills up.
   */
  group?: readonly AvatarGroupPerson[];
  size?: AvatarSize;
  className?: string;
  testId?: string;
}

export function PersonLabel({
  label,
  isPerson,
  avatarUrl,
  group,
  size = "sm",
  className,
  testId,
}: PersonLabelProps): ReactNode {
  const showGroup = !isPerson && group !== undefined && group.length > 0;

  return (
    <span className={cn("inline-flex items-center gap-2", className)}>
      {showGroup ? <AvatarGroup people={group} size={size} /> : null}
      {/*
        `?? null` rather than passing `avatarUrl` straight through: `exactOptionalPropertyTypes` is
        on, so an explicit `undefined` is not the same as an omitted prop. Null and absent mean the
        same thing to `Avatar` — no picture — and only one of them type-checks.
      */}
      {showGroup ? null : (
        <Avatar
          src={isPerson ? (avatarUrl ?? null) : null}
          {...(isPerson ? { name: label } : {})}
          size={size}
          {...(testId === undefined ? {} : { testId })}
        />
      )}
      <span className="truncate" title={label}>
        {isPerson ? firstNameOf(label) : label}
      </span>
    </span>
  );
}
